import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PropertyCard from "./PropertyCard";
import { useAuth } from "../lib/AuthContext";
import { Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { PropertyGridSkeleton } from "../components/Skeletons";
import usePageTitle from "../lib/usePageTitle";
import PageHeader from "../shared/components/PageHeader";

/**
 * v2.34 — PART 7: Saved Properties / Favorites for clients.
 * Shows the user's saved listings (Heart icon on cards). Anon users hit
 * a soft prompt to log in.
 */
export default function SavedProperties() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageTitle("Saved Properties | HouseEntry");

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    let alive = true;
    setLoading(true);
    api.get("/me/saved").then((r) => {
      if (!alive) return;
      setItems(r.data?.results || []);
    }).catch(() => {
      if (alive) setItems([]);
    }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6 bg-[#FAFAFA]">
        <div className="max-w-md w-full bg-white border border-[#E4E4E7] rounded-sm p-10 text-center" data-testid="saved-anon-prompt">
          <Heart size={28} className="mx-auto text-[#DC2626] mb-3" />
          <h1 className="font-heading font-bold text-2xl tracking-tight">Save Your Favorites</h1>
          <p className="text-[14px] text-[#52525B] mt-3 mb-6">
            Log in to bookmark properties and revisit them anytime — across devices.
          </p>
          <Link to="/login?next=/saved">
            <Button className="btn-brand rounded-sm" data-testid="saved-login-cta">Log in to view</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* v2.71.51 — Integrated header replaces the floating Back pill. */}
      <div className="sticky top-0 z-30 bg-white border-b border-[#E4E4E7]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <PageHeader title="Saved properties" backLabel="Home" home="/" borderless />
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#DC2626] mb-2 flex items-center gap-2">
            <Heart size={12} fill="currentColor" /> Your Collection
          </div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#09090B]">Saved Properties</h1>
          <p className="text-[14px] text-[#52525B] mt-2" data-testid="saved-count-summary">
            {loading ? "Loading…" : items.length === 0 ? "Nothing saved yet — tap the heart on any listing." : `${items.length} saved listing${items.length === 1 ? "" : "s"}.`}
          </p>
        </div>

        {loading ? (
          <PropertyGridSkeleton count={6} />
        ) : items.length === 0 ? (
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-12 text-center" data-testid="saved-empty-state">
            <Heart size={32} className="mx-auto text-[#A1A1AA] mb-4" />
            <h2 className="font-heading font-semibold text-xl tracking-tight">No saved properties</h2>
            <p className="text-[14px] text-[#52525B] mt-2 mb-6">
              Browse listings and tap the heart icon to save the ones you like for later.
            </p>
            <Link to="/">
              <Button className="btn-brand rounded-sm" data-testid="saved-browse-cta">Browse Properties</Button>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="saved-grid">
            {items.map((p, i) => (
              <PropertyCard
                key={p.id}
                p={p}
                index={i}
                onSaveChange={(pid, saved) => {
                  // When user un-saves from the /saved page, drop the card
                  // from the visible grid so the list stays accurate.
                  if (!saved) setItems((prev) => prev.filter((x) => x.id !== pid));
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
